import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../ui/accordion";

const match = (kw, ...texts) => !kw || texts.some((x) => String(x).toLowerCase().includes(kw));

export function Hi({ text, kw }) {
  const str = String(text);
  const i = kw ? str.toLowerCase().indexOf(kw) : -1;
  if (i < 0) return <span>{str}</span>;
  const a = str.slice(0, i), b = str.slice(i, i + kw.length), c = str.slice(i + kw.length);
  return <span>{a}<mark className="bg-amber-200 rounded px-0.5">{b}</mark>{c}</span>;
}

export function SopList({ items, kw }) {
  const rows = items.map(([t, steps]) => [t, steps.filter((s) => match(kw, s, t))]).filter(([t, steps]) => steps.length || match(kw, t));
  if (!rows.length) return <p className="text-sm text-slate-400" data-testid="guide-sop-empty">Tidak ada SOP yang cocok.</p>;
  return (
    <Accordion type="multiple" defaultValue={kw ? rows.map(([t]) => t) : []} className="surface px-4" data-testid="guide-sop">
      {rows.map(([t, steps]) => (
        <AccordionItem key={t} value={t}>
          <AccordionTrigger className="text-sm font-semibold text-left"><Hi text={t} kw={kw} /></AccordionTrigger>
          <AccordionContent>
            <ol className="space-y-1.5">{steps.map((s, i) => <li key={s} className="flex gap-2 text-sm text-slate-600"><span className="h-5 w-5 shrink-0 rounded-full bg-indigo-600 text-white grid place-items-center text-[10px] font-bold">{i + 1}</span><Hi text={s} kw={kw} /></li>)}</ol>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function FieldGuide({ items, kw }) {
  const rows = items.map(([f, cols]) => [f, cols.filter(([c, d]) => match(kw, c, d, f))]).filter(([, cols]) => cols.length);
  if (!rows.length) return <p className="text-sm text-slate-400" data-testid="guide-fields-empty">Tidak ada kolom yang cocok.</p>;
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="guide-fields">
      {rows.map(([f, cols]) => (
        <div key={f} className="surface p-4">
          <p className="font-semibold text-sm mb-2"><Hi text={f} kw={kw} /></p>
          <table className="w-full text-xs"><tbody>{cols.map(([c, d]) => <tr key={c} className="border-t"><td className="py-1.5 pr-2 font-medium text-slate-700 whitespace-nowrap align-top w-36"><Hi text={c} kw={kw} /></td><td className="py-1.5 text-slate-600"><Hi text={d} kw={kw} /></td></tr>)}</tbody></table>
        </div>
      ))}
    </div>
  );
}

// Flatten tree iteratively (no recursive component: avoids babel plugin recursion)
function flatten(root, kw) {
  const out = [];
  const keep = (n) => match(kw, n.label) || (n.children || []).some(keep);
  const stack = [[root, 0, "r"]];
  while (stack.length) {
    const [n, depth, id] = stack.pop();
    if (!keep(n)) continue;
    const kids = (n.children || []).filter(keep);
    out.push({ id, label: n.label, depth, hasKids: kids.length > 0, parent: id.split(".").slice(0, -1).join(".") });
    for (let i = kids.length - 1; i >= 0; i--) stack.push([kids[i], depth + 1, `${id}.${i}`]);
  }
  return out;
}

export function MindMap({ root, kw }) {
  const [closed, setClosed] = useState({});
  const rows = flatten(root, kw);
  if (!rows.length) return <p className="text-sm text-slate-400" data-testid="guide-mindmap-empty">Tidak ada cabang yang cocok.</p>;
  const hidden = (r) => { let p = r.parent; while (p) { if (closed[p]) return true; p = p.split(".").slice(0, -1).join("."); } return false; };
  return (
    <div className="surface p-5 overflow-x-auto" data-testid="guide-mindmap">
      <ul className="space-y-1">
        {rows.filter((r) => !hidden(r)).map((r) => (
          <li key={r.id} data-testid="mindmap-node" style={{ marginLeft: `${r.depth * 20}px` }} className={`flex items-center gap-1.5 ${r.depth === 0 ? "font-heading font-bold text-base" : r.depth === 1 ? "font-semibold text-sm" : "text-xs text-slate-600"} ${r.depth > 0 ? "border-l border-slate-200 pl-3" : ""}`}>
            {r.hasKids ? <button type="button" data-testid="mindmap-toggle" onClick={() => setClosed({ ...closed, [r.id]: !closed[r.id] })} className="text-slate-400 hover:text-slate-900">{closed[r.id] ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button> : <span className="w-3.5 text-center text-slate-300">•</span>}
            <span className={`rounded px-1.5 py-0.5 ${r.depth === 0 ? "bg-slate-900 text-white" : r.depth === 1 ? "bg-indigo-50 text-indigo-900" : ""}`}><Hi text={r.label} kw={kw} /></span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-slate-400 mt-3">Pohon pikiran = struktur menu utama → sub-fitur. Klik panah untuk melipat cabang.</p>
    </div>
  );
}
